import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Search,
  Edit,
  Printer,
  ChevronLeft,
  ChevronRight,
  Copy,
  FileDown,
} from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { paymentsService } from '@/services/paymentsService'
import { billsService } from '@/services/billsService'
import { Payment, BillVendor } from '@/api/types'

export const PaymentsList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [payments, setPayments] = useState<Payment[]>([])
  const [vendors, setVendors] = useState<BillVendor[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedVendorId, setSelectedVendorId] = useState<number | undefined>(undefined)
  const [advanceAmounts, setAdvanceAmounts] = useState<Record<number, number>>({}) // paymentId -> advanceAmount

  const branchId = user?.selectedBranch?.branchId

  // Load vendors for filter
  useEffect(() => {
    if (!branchId) return
    const loadVendors = async () => {
      try {
        const vendorList = await billsService.getAllVendors(branchId)
        setVendors(vendorList)
      } catch (error: any) {
        // Error handled silently
      }
    }
    loadVendors()
  }, [branchId])

  // Load payments
  useEffect(() => {
    if (!branchId) {
      return
    }

    const loadPayments = async () => {
      try {
        setLoading(true)
        const response = await paymentsService.getAllPayments(branchId, pageNumber, pageSize)
        const list = response.data || []
        setPayments(list)
        setTotalCount(response.totalRecords || list.length || 0)
        const pages = response.totalRecords ? Math.ceil(response.totalRecords / response.pageSize) : 0
        setTotalPages(pages || 1)

        // Load advance amounts for payments that have advanceId
        const paymentsWithAdvance = list.filter((payment: Payment) => payment.advanceId && payment.advanceId > 0)
        if (paymentsWithAdvance.length > 0) {
          const advanceAmountsMap: Record<number, number> = {}
          
          // Fetch advance lines for each payment with advanceId
          await Promise.all(
            paymentsWithAdvance.map(async (payment: Payment) => {
              if (payment.paymentId && payment.advanceId) {
                try {
                  const advanceLines = await paymentsService.getVendorAdvanceLineByAdvanceId(payment.advanceId)
                  // Filter advance lines for this specific payment
                  const paymentAdvanceLines = advanceLines.filter((line: any) => 
                    line.paymentId === payment.paymentId
                  )
                  const totalAdvanceAmount = paymentAdvanceLines.reduce((sum: number, line: any) => {
                    return sum + (line.amount || 0)
                  }, 0)
                  advanceAmountsMap[payment.paymentId] = totalAdvanceAmount
                } catch (error) {
                  // If advance lines fetch fails, try to get advance amount from payment.advance
                  // For now, set to 0 if we can't fetch
                  advanceAmountsMap[payment.paymentId] = payment.advance || 0
                }
              }
            })
          )
          
          setAdvanceAmounts(advanceAmountsMap)
        }
      } catch (error: any) {
        const status = error.response?.status
        const msg = (error.response?.data?.message || '').toLowerCase()
        const isNoData = status === 404 || /not found|no data|no records/.test(msg)
        setPayments([])
        setTotalCount(0)
        setTotalPages(0)
        if (!isNoData) {
          toast.error(error.response?.data?.message || 'Failed to load payments')
        }
      } finally {
        setLoading(false)
      }
    }

    loadPayments()
  }, [branchId, pageNumber, pageSize])

  const filteredPayments = useMemo(() => {
    return payments.filter((payment) => {
      if (selectedVendorId && payment.vendorId !== selectedVendorId) {
        return false
      }

      if (!searchQuery.trim()) return true
      const query = searchQuery.toLowerCase()
      return (
        (payment.paymentNo || '').toLowerCase().includes(query) ||
        (payment.vendorName || '').toLowerCase().includes(query) ||
        (payment.billNo || '').toLowerCase().includes(query) ||
        (payment.refNo || '').toLowerCase().includes(query)
      )
    })
  }, [payments, selectedVendorId, searchQuery])

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.00'
    return Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const csvEscape = (cell: string | number) => `"${String(cell).replace(/"/g, '""')}"`

  const handleCopy = async () => {
    if (filteredPayments.length === 0) {
      toast.error(t('payments.nothingToCopy', { defaultValue: 'Nothing to copy.' }))
      return
    }
    const header = ['Payment No', 'Vendor', 'Bill No', 'Ref No', 'Date', 'Amount', 'Advance'].join('\t')
    const lines = filteredPayments.map((p) => {
      const pid = p.paymentId ?? 0
      const advAmt = pid ? (advanceAmounts[pid] ?? 0) : 0
      return [
        p.paymentNo ?? '',
        p.vendorName ?? '',
        p.billNo ?? '',
        p.refNo ?? '',
        formatDate(p.paymentDate as any),
        String(p.amount ?? ''),
        String(advAmt ?? ''),
      ].join('\t')
    })
    await navigator.clipboard.writeText([header, ...lines].join('\n'))
    toast.success(t('payments.copiedToClipboard', { defaultValue: 'Copied to clipboard.' }))
  }

  const handleCsv = () => {
    if (filteredPayments.length === 0) {
      toast.error(t('payments.nothingToExport', { defaultValue: 'Nothing to export.' }))
      return
    }
    const header = ['Payment No', 'Vendor', 'Bill No', 'Ref No', 'Date', 'Amount', 'Advance'].map(csvEscape).join(',')
    const body = filteredPayments
      .map((p) => {
        const pid = p.paymentId ?? 0
        const advAmt = pid ? (advanceAmounts[pid] ?? 0) : 0
        return [
          p.paymentNo ?? '',
          p.vendorName ?? '',
          p.billNo ?? '',
          p.refNo ?? '',
          formatDate(p.paymentDate as any),
          String(p.amount ?? ''),
          String(advAmt ?? ''),
        ]
          .map(csvEscape)
          .join(',')
      })
      .join('\n')
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `bill_payments_${new Date().toISOString().slice(0, 10)}.csv`
    link.click()
    window.URL.revokeObjectURL(url)
    toast.success(t('payments.csvDownloaded', { defaultValue: 'CSV downloaded.' }))
  }

  const handleAddPayment = () => {
    navigate('/purchases/payments/new')
  }

  const handleEditPayment = (payment: Payment) => {
    const paymentId = payment.paymentId
    if (!paymentId) {
      toast.error(t('payments.invalidPaymentId'))
      return
    }
    navigate(`/purchases/payments/${paymentId}/edit`)
  }

  const handlePrint = (_payment: Payment) => {
    toast.success(t('payments.print') + ' ' + t('common.comingSoon') || 'Print functionality coming soon')
  }

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value))
    setPageNumber(1)
  }

  const handleVendorFilterChange = (value: string) => {
    if (value === 'all') {
      setSelectedVendorId(undefined)
    } else {
      setSelectedVendorId(Number(value))
    }
    setPageNumber(1)
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title={t('payments.title')}
          description={t('payments.managePayments')}
        />
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title={t('payments.branchNotSelected')}
              description={t('payments.selectBranchToViewPayments')}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={t('payments.title')}
        description={t('payments.managePayments')}
        action={{
          label: t('payments.addNew'),
          onClick: handleAddPayment,
          icon: Plus,
        }}
      />

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <label className="text-sm font-medium mb-2 block">{t('payments.vendor')}</label>
              <Select value={selectedVendorId ? String(selectedVendorId) : 'all'} onValueChange={handleVendorFilterChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t('payments.all')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('payments.all')}</SelectItem>
                  {vendors.map(vendor => (
                    <SelectItem key={vendor.vendorId} value={String(vendor.vendorId)}>
                      {vendor.vendorNameEn || vendor.vendorNameAr || `Vendor ${vendor.vendorId}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 w-full">
              <label className="text-sm font-medium mb-2 block">{t('payments.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('payments.search')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => void handleCopy()}>
                <Copy className="mr-2 h-4 w-4" />
                {t('payments.copy', { defaultValue: 'Copy' })}
              </Button>
              <Button variant="outline" size="sm" onClick={handleCsv}>
                <FileDown className="mr-2 h-4 w-4" />
                CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payments Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">{t('common.loading')}...</p>
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title={searchQuery || selectedVendorId ? t('payments.noPaymentsFound') : t('payments.noPaymentsYet')}
                description={
                  searchQuery || selectedVendorId
                    ? t('payments.tryAdjustingSearch')
                    : t('payments.getStarted')
                }
                action={
                  !searchQuery && !selectedVendorId
                    ? {
                        label: t('payments.addNewPayment'),
                        onClick: handleAddPayment,
                      }
                    : undefined
                }
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('payments.paymentNo')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('payments.vendor')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('payments.date')}
                      </TableHead>
                      <TableHead className="hidden md:table-cell h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('payments.advance')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('payments.amount')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center w-[100px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('payments.print')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center w-[100px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('payments.edit')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPayments.map((payment) => {
                      const paymentId = payment.paymentId
                      if (!paymentId) return null
                      return (
                        <TableRow key={paymentId} className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors">
                          <TableCell className="px-4 py-2 text-sm text-center align-middle font-medium">
                            {payment.paymentNo || `PAY-${paymentId}`}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {payment.vendorName || payment.vendorNameEn || payment.vendorNameAr || 'N/A'}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {formatDate(payment.paymentDate)}
                          </TableCell>
                          <TableCell className="hidden md:table-cell px-4 py-2 text-sm text-center align-middle">
                            {payment.advanceId && payment.advanceId > 0
                              ? formatAmount(advanceAmounts[paymentId] || payment.advance || 0)
                              : formatAmount(0)}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {formatAmount(payment.amount)}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-center align-middle">
                            <div className="flex justify-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePrint(payment)}
                                title={t('payments.printPayment')}
                                className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                              >
                                <Printer className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="px-4 py-2 text-center align-middle">
                            <div className="flex justify-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditPayment(payment)}
                                title={t('payments.editPayment')}
                                className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="border-t px-4 sm:px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground text-center sm:text-left">
                  {t('common.showing')} {(pageNumber - 1) * pageSize + 1} {t('common.to')}{' '}
                  {Math.min(pageNumber * pageSize, totalCount)} {t('common.of')} {totalCount} {t('common.entries')}
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">{t('common.pageSize')}:</span>
                    <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                    disabled={pageNumber === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('common.previous')}</span>
                  </Button>
                  <div className="text-sm whitespace-nowrap">
                    {t('common.page')} {pageNumber} {t('common.of')} {totalPages || 1}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => (prev < totalPages ? prev + 1 : prev))}
                    disabled={pageNumber >= totalPages}
                  >
                    <span className="hidden sm:inline">{t('common.next')}</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

